import * as ChannelService from '@channel.io/channel-web-sdk-loader'
import { useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import axios from 'axios'
import styled from 'styled-components'

import { MODALS_ID } from '../../constants'

import { toast } from '../../utils'
import { abrCompleteDepositAccountRegister, abrStartDepositAccountRegister } from '../../utils/abr'

import { StoresContext } from '../../provider/StoreProvider'
import { ModalContext } from '../../provider/modalProvider'

import { useNavigate } from '../../hooks/useNavigate'

import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { ColumnItem } from '../../components/common'
import { Button } from '../../components/button'
import { Text } from '../../components/text'
import { TermsBox } from '../../components/termsBox'
import { Footer } from '../../components/footer'
import { Header } from '../../components/header'
import { TermsDetailModal } from '../../components/modals/TermsDetailModal'

export const DepositTerms = observer(() => {
  const {
    authStore: { userInfo },
    commonStore: { termsListStore, setShowTab, setInvalidAccess },
    myStore,
  } = useContext(StoresContext)
  const { openModal, closeModal } = useContext(ModalContext)

  const [terms, setTerms] = useState<{ termsId: number; approved: boolean; required: boolean }[]>([])
  const [termsId, setTermsId] = useState<number | undefined>()

  const isDepositAccount = !!userInfo?.depositAccount

  const navigate = useNavigate()

  const openTermsDetailModal = () => {
    openModal({
      id: MODALS_ID.TERMS_DETAIL,
      Component: () => (
        <TermsDetailModal
          termsId={termsId}
          isScrollEvent={true}
          buttonProps={{ type: 'primary' }}
          onDismiss={(result) => {
            setTermsId(undefined)
            closeModal(MODALS_ID.TERMS_DETAIL)
            if (!result) return false

            setTerms((prevState) => prevState.map((v) => (v.termsId === termsId ? { ...v, approved: true } : v)))
          }}
        />
      ),
    })
  }

  useEffect(() => {
    setShowTab(false)
    termsListStore.load({ param: { pageName: 'DEPOSIT_ACCOUNT' } })
    abrStartDepositAccountRegister()
    ChannelService.track('start_deposit_account_register')
  }, [])

  useEffect(() => {
    if (typeof termsId === 'number') openTermsDetailModal()
  }, [termsId])

  useEffect(() => {
    if (!termsListStore.content) return
    setTerms(termsListStore.content.map(({ id, required }) => ({ required, termsId: id, approved: false })))
  }, [termsListStore.content])

  useEffect(() => {
    if (isDepositAccount) {
      setInvalidAccess(true)
    }
  }, [userInfo])

  if (isDepositAccount) {
    return <></>
  }

  const createDepositAccount = async () => {
    try {
      const response = await myStore.createDepositAccount(terms.map(({ termsId, approved }) => ({ termsId, approved })))

      if (!response) {
        toast('예치금 계좌 생성에 실패했어요.', response)
        return
      }

      abrCompleteDepositAccountRegister()
      ChannelService.track('complete_deposit_account_Register')
      navigate('/account/deposit/success', { replace: true, state: { prevPathname: window.location.pathname } })
    } catch (error) {
      const defaultErrorMessage = '예치금 계좌 생성에 실패했어요.'
      if (axios.isAxiosError(error)) {
        toast(error.response?.data.message || defaultErrorMessage)
        return
      }
      toast(defaultErrorMessage)
    }
  }

  return (
    <Wrap>
      <Content>
        <Header type="SUB" title="예치금 계좌 생성하기" />
        <Container>
          <Text size={24} weight={600} lineHeight={34}>
            아래의 약관에 동의 시
            <br />
            예치금 충전 계좌 발급이 시작돼요.
          </Text>
          <Terms $gap={16}>
            {termsListStore.content?.map(({ id, name, required }) => {
              const isAgreed = terms.find(({ termsId }) => termsId === id)?.approved ?? false
              return (
                <TermsBox
                  key={id}
                  title={`(${required ? '필수' : '선택'}) ${name}`}
                  isAgreed={isAgreed}
                  onChange={(checked) =>
                    checked
                      ? setTermsId(id)
                      : setTerms((prevState) =>
                          prevState.map((v) => (v.termsId === id ? { ...v, approved: false } : v)),
                        )
                  }
                />
              )
            })}
          </Terms>
        </Container>
      </Content>
      <Footer>
        <Button
          type="primary"
          size="large"
          disabled={!terms.every(({ approved, required }) => (required ? approved : true))}
          block={true}
          onClick={createDepositAccount}
        >
          다음
        </Button>
      </Footer>
    </Wrap>
  )
})

const Container = styled.div`
  padding: 56px 24px 24px;
`

const Terms = styled(ColumnItem)`
  margin-top: 32px;
`
